import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIdentity } from "@/features/identity/hooks/useIdentity";
import { useMyLinks } from "@/features/identity/hooks/useMyLinks";
import { useUnsavedWarning } from "@/features/identity/hooks/useUnsavedWarning";
import { IDLE } from "@/features/identity/hooks/useSectionForm";
import { buildStructuredIdentity } from "@/features/identity/buildStructuredIdentity";
import { getMyPrimaryCard, savePrimaryCard } from "@/features/cards/api/cardsApi";
import { DEFAULT_TEMPLATE_ID, getTemplate, normalizeDesignConfig } from "@/features/cards/templates/cardBackgrounds";
import { slugify, validateSlug, validateCardName, normalizeSlugInput } from "@/features/cards/slug";
import { useSlugAvailability } from "@/features/cards/hooks/useSlugAvailability";
import { getCanonicalProfileUrl } from "@/lib/canonicalUrl";
import { friendlyDataError } from "@/lib/errors";

const fromCard = (c) => ({ name: c.name, slug: c.slug, template_id: getTemplate(c.template_id).id, design_config: normalizeDesignConfig(c.template_id, c.design_config) });
const fromProfile = (p) => ({ name: p.full_name || "My card", slug: slugify(p.full_name || ""), template_id: DEFAULT_TEMPLATE_ID, design_config: normalizeDesignConfig(DEFAULT_TEMPLATE_ID) });

function saveErrorMessage(err) {
  if (err?.code === "23505" && err.message?.includes("slug")) return "That link is already taken. Try another.";
  if (err?.code === "23505") return "You already have a primary card. We reloaded it — please try again.";
  if (err?.code === "23514") return "Some card details aren't valid. Check the name and link.";
  return friendlyDataError(err, "We couldn't save your card. Please try again.");
}

export function useCardEditor() {
  const { profile } = useIdentity();
  const links = useMyLinks();
  const [card, setCard] = useState(null);
  const [draft, setDraft] = useState(null);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [status, setStatus] = useState(IDLE);
  const [errors, setErrors] = useState({});
  const saving = useRef(false);

  const fetchCard = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const existing = await getMyPrimaryCard();
      setCard(existing);
      setDraft(existing ? fromCard(existing) : fromProfile(profile));
      setLoad({ status: "ready", error: null });
    } catch (err) {
      setLoad({ status: "error", error: friendlyDataError(err, "We couldn't load your card.") });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const baseline = useMemo(() => (card ? fromCard(card) : null), [card]);
  const dirty = Boolean(draft) && JSON.stringify(draft) !== JSON.stringify(baseline);
  useUnsavedWarning(dirty);
  const slugCheck = useSlugAvailability(draft?.slug, card?.slug);

  const change = (patch) => {
    setDraft((d) => ({ ...d, ...patch }));
    setErrors({});
    setStatus(IDLE);
  };
  const setTemplate = (id) => change({ template_id: id, design_config: normalizeDesignConfig() });
  const setName = (name) => change({ name });
  const setSlug = (value) => change({ slug: normalizeSlugInput(value) });

  const save = async () => {
    if (saving.current || !draft) return false;
    const found = Object.fromEntries(
      Object.entries({ name: validateCardName(draft.name), slug: validateSlug(draft.slug) }).filter(([, v]) => v),
    );
    if (!found.slug && slugCheck.state === "taken") found.slug = "That link is already taken. Try another.";
    setErrors(found);
    if (Object.keys(found).length) {
      setStatus({ state: "invalid", message: "Please check the card name and link." });
      return false;
    }
    saving.current = true;
    setStatus({ state: "saving", message: null });
    try {
      const saved = await savePrimaryCard(draft);
      setCard(saved);
      setDraft(fromCard(saved));
      setStatus({ state: "saved", message: "Draft saved." });
      return true;
    } catch (err) {
      if (err?.code === "23505" && !err.message?.includes("slug")) await fetchCard();
      setStatus({ state: "error", message: saveErrorMessage(err) });
      return false;
    } finally {
      saving.current = false;
    }
  };

  const liveSlug = draft && !validateSlug(draft.slug) ? draft.slug : null;
  const identity = useMemo(
    () => buildStructuredIdentity(profile, links.links, { canonicalUrl: getCanonicalProfileUrl(liveSlug) }),
    [profile, links.links, liveSlug],
  );

  return { card, draft, load, status, errors, dirty, slugCheck, identity, setTemplate, setName, setSlug, save, reload: fetchCard };
}
